/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-const */
import { CsbHelper, EnvironmentConfig, FilterOptions, getConfig, Job } from '@recruiting-solutions/rs-shared';
import { BehaviorSubject, Observable } from 'rxjs';
import { Facet, FacetItem } from '../models';

import { SearchOptions } from '../models/searchOptions';
import { SearchParams } from '../models/searchParams';
import { SearchRequest } from '../models/searchRequest';
import { SearchResponse, SearchResultOptions } from '../models/searchResponse';
import { Distance, Unit } from '../models/distance';
import { locationService } from './location.service';

class SearchServiceController {
    private defaultOptions?: {
        defaultOrder?: { isDesc: boolean; orderBy: string };
        useLanguageFilter?: boolean;
        pageSize?: number;
        searchRadius?: number;
        appendLocaleToFilters?: boolean;
        brandFilters?: FilterOptions[];
        getCountryCodes?: () => Promise<string>;
        searchUrl?: string;
        useProxy?: boolean;
    };
    private apiUrl?: string;
    public keyword?: string | null;
    private pageSize = 10;
    private currentPage = 1;
    private orderBy?: string | null;
    private isDesc?: boolean;
    private regionFilter?: string | null = '';
    private region?: string;
    private coordinates: GeolocationCoordinates | undefined;
    private defaultRadius = 50;
    private searchRadius?: Distance | null;
    private apiKey = '8DE66DF51831A58E3317536F02E737BB';
    private detectedLanguage?: string;
    private detectedIsInternal = false;
    private getCountryCodes?: () => Promise<string>;

    public useProxy = false;
    public searchUrl?: string;
    public useLanguageFilter = false;
    public technicalFilter = '';
    public filters?: { name: string; isList: boolean; values: string[] }[];
    public facets?: FacetItem[];
    public totalCount: any;
    public maxLocations?: number;
    public brandFilter?: string;
    public brandFilters?: FilterOptions[];
    public locale?: string;
    public internal?: boolean = undefined;
    public currentOptionsSource: BehaviorSubject<SearchOptions | undefined> = new BehaviorSubject<SearchOptions | undefined>(undefined);
    public currentOptions: Observable<SearchOptions | undefined> = this.currentOptionsSource.asObservable();
    public currentFacetsSource: BehaviorSubject<any> = new BehaviorSubject<{ [key: string]: Facet[] } | undefined>(undefined);
    public currentFacets: Observable<{ [key: string]: Facet[] } | undefined> = this.currentFacetsSource.asObservable();
    public currentJobsSource: BehaviorSubject<SearchResponse | undefined> = new BehaviorSubject<SearchResponse | undefined>(undefined);
    public currentJobs: Observable<SearchResponse | undefined> = this.currentJobsSource.asObservable();
    private _isInitializing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public isInitializing: Observable<boolean> = this._isInitializing.asObservable();
    public brand: { brand: string; override: boolean } | undefined;
    public config: EnvironmentConfig | undefined;
    public language?: string;
    public isInternal = false;
    public enableIsInternalFilter = false;
    public brandFieldName = 'brand';
    jobIdsFilter?: string[];

    async init(options?: {
        defaultOrder?: { isDesc: boolean; orderBy: string };
        useLanguageFilter?: boolean;
        pageSize?: number;
        searchRadius?: number;
        appendLocaleToFilters?: boolean;
        brandFilters?: FilterOptions[];
        getCountryCodes?: () => Promise<string>;
        searchUrl?: string;
        useProxy?: boolean;
    }) {
        this.defaultOptions = options;
        this.getCountryCodes = options?.getCountryCodes;
        locationService.currentLocation?.subscribe(this.setLocation.bind(this));
        this.config = getConfig();
        // fallback to staging index if not configured properly
        this.useProxy = !!options?.useProxy;
        this.searchUrl = options?.searchUrl || this.config.indexUrl || 'https://csbep.search.windows.net/';
        this.apiKey = this.config.indexApiKey || this.apiKey;
        this.facets = this.config.facets;
        if (options) {
            this.useLanguageFilter = !!options.useLanguageFilter;
            if (options.pageSize) {
                this.pageSize = options.pageSize;
            }
            if (options.defaultOrder) {
                this.orderBy = options.defaultOrder.orderBy;
                this.isDesc = options.defaultOrder.isDesc;
            }
            if (options.brandFilters) {
                this.brandFilters = options.brandFilters;
            }
            if (options.searchRadius) {
                this.searchRadius = new Distance(options.searchRadius, Unit.Kilometers);
            }
        }

        const locale = CsbHelper.getLocale();
        console.log('Setting search detectedLanguage to ' + locale);
        this.detectedLanguage = locale;

        this.internal = CsbHelper.getInternal();
        if (this.internal) {
            this.facets = this.config.internalFacets;
        }
        if (options && options.appendLocaleToFilters) {
            for (const facet of this.facets || []) {
                if (facet.isComplexObject) {
                    facet.name = `${facet.name}/${facet.name}_${locale}`;
                }
            }
        }
        await this.initializeState();
        this._isInitializing.next(false);

        CsbHelper.getBrand().then(b => (this.brand = b));
    }

    private async initializeState() {
        this.filters = this.facets?.map(f => {
            return {
                name: f.name,
                isList: f.isList,
                isComplexObject: f.isComplexObject,
                values: [],
            };
        });
        const state = await this.loadState();

        this.currentOptionsSource.next(state);
    }

    private setLocation(location: { key: string; location: GeolocationCoordinates } | undefined) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        if (location) {
            this.coordinates = location.location;
            this.region = location.key;
        } else {
            this.coordinates = undefined;
        }

        const changes = [];
        if (newParams.coordinates !== this.coordinates) {
            newParams.coordinates = this.coordinates;
            changes.push('coordinates');
        }

        if (newParams.region !== this.region) {
            newParams.region = this.region;
            changes.push('region');
        }

        if (changes.length) {
            this.currentOptionsSource.next({ changes: changes, params: newParams });
        }
    }

    getSearchLanguage() {
        return this.language || this.detectedLanguage;
    }

    getIndexName() {
        const indexName = getConfig().indexName;
        this.internal = CsbHelper.getInternal();
        if (this.internal) return indexName + '-internal';

        return indexName;
    }

    hasValidCoordinates() {
        if (this.coordinates && this.coordinates.longitude && this.coordinates.latitude) {
            return true;
        }
        return false;
    }

    getBrandFilter() {
        if (this.brandFilters) {
            if (this.brandFilters.find(x => x.key === 'Germany')) {
                const res = this.brandFilters.map(bf => bf.key);
                res.push('dmTECH');
                return res;
            }
            return this.brandFilters.map(bf => bf.key);
        } else if (this.brandFilter) {
            return [this.brandFilter];
        }
        return [];
    }

    async pingIndex(indexUrl: string, indexName: string, indexKey: string): Promise<any> {
        return new Promise((res, rej) => {

            const timeout = setTimeout(() => {
                // index is not reachable, use fallback
                rej();
            }, 2000);
            fetch(`${indexUrl}indexes/${indexName}/docs/search?api-version=2019-05-06`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'api-key': indexKey
                },
                body: JSON.stringify({
                    top: 1,
                    searchFields: 'jobId',
                    search: "*"
                })
            }).then(() => {
                clearTimeout(timeout);
                res(undefined);
            })
                .catch(() => {
                    rej();
                });
        });
    }

    async search(searchRequest: SearchRequest, updateFacets = false, saveState = false): Promise<SearchResponse | undefined> {
        const url = `${this.getDocsUrl()}/search?api-version=2020-06-30&`;

        if (saveState) {
            this.saveState();
        }
        await this.ensureInitialized();
        searchRequest = this.setLanguageFilter(searchRequest);
        searchRequest = this.setTechnicalFilter(searchRequest);
        searchRequest.setBrandFilter(this.getBrandFilter());
        searchRequest.setDatePostedFilter();

        const response = await this.post(url, searchRequest);

        if (updateFacets) {
            this.currentFacetsSource.next(response?.facets);
        }

        return response;
    }

    async getFacet(facetName: string): Promise<Facet[]> {
        let url = `${this.getDocsUrl()}/search?api-version=2020-06-30`;
        let searchRequest: SearchRequest = new SearchRequest('*');

        await this.ensureInitialized();
        searchRequest = this.setLanguageFilter(searchRequest);
        searchRequest = this.setTechnicalFilter(searchRequest);
        searchRequest.setBrandFilter(this.getBrandFilter());
        searchRequest.setDatePostedFilter();
        searchRequest.facets = [];
        searchRequest.facets.push(`${facetName},count:5000`);
        searchRequest.top = 0;
        let response = await this.post(url, searchRequest);
        // Just .get throws an exception.
        return (response?.facets as any)?.[facetName];
    }

    async getSearchResult(options?: SearchResultOptions): Promise<SearchResponse> {
        // updateFacets = false, getAll?: boolean, pageSize?: number, usePostalCode = true): Promise<SearchResponse> {
        if (!options) {
            options = {};
        }
        // Only explicitly set saveState are not allowed to save the state, all others should do it. saveState is therefore by default true
        if (options.saveState !== false) {
            this.saveState();
        }
        if (options.persistUrl) {
            this.persistUrl();
        }
        const url = `${this.getDocsUrl()}/search?api-version=2020-06-30`;

        let searchRequest: SearchRequest = new SearchRequest('*');
        searchRequest.setDatePostedFilter();
        if (this.jobIdsFilter && this.jobIdsFilter.length) {
            searchRequest.setJobIdsFilter(this.jobIdsFilter);
        }
        let fields = getConfig()?.searchFields?.join(','); //"jobId,title,textDescription,tasks,profile,keyWords,jobType,jobTypes,jobLevel,jobLevels,jobSector,jobSectors,jobFunction,jobFunctions";
        if (!this.keyword) {
            if (locationService.locationSearch && !this.hasValidCoordinates() && !this.regionFilter) {
                //get coords
                if (options.usePostalCode) {
                    fields = 'addresses/city,addresses/postalCode';
                } else {
                    fields = 'addresses/city';
                }
                searchRequest.search = `${locationService.locationSearch}*`;
            } else {
                searchRequest.search = '*';
                if ((!this.orderBy) && this.hasValidCoordinates()) {
                    searchRequest.orderby = `geo.distance(location, geography'POINT(${this.coordinates?.longitude} ${this.coordinates?.latitude})')`;
                }
            }
        } else {
            if (locationService.locationSearch && !this.regionFilter) {
                if (!this.config?.ignoreLocationInFullTextSearch) {
                    if (options.usePostalCode) {
                        fields += ',addresses/city,addresses/postalCode';
                    } else {
                        fields += ',addresses/city';
                    }
                    if (this.keyword.indexOf(' ') > -1) {
                        searchRequest.search = `${this.keyword} | ${this.keyword}* | ${locationService.locationSearch}*`;
                    } else {
                        searchRequest.search = `/.*${this.keyword}.*/ OR /.*${locationService.locationSearch}.*/`;
                        searchRequest.queryType = 'full';
                    }
                } else {
                    if (this.keyword.indexOf(' ') > -1) {
                        searchRequest.search = `${this.keyword} | ${this.keyword}*`;
                    } else {
                        searchRequest.search = `/.*${this.keyword}.*/`;
                        searchRequest.queryType = 'full';
                    }
                }
            } else {
                if (this.keyword.indexOf(' ') > -1) {
                    searchRequest.search = `${this.keyword} | ${this.keyword}*`;
                } else {
                    searchRequest.search = `/.*${this.keyword}.*/`;
                    searchRequest.queryType = 'full';
                }
            }
        }
        searchRequest.searchFields = fields;

        this.filters?.forEach(filte => {
            if (filte.values.length) {
                if (filte.name.startsWith('addresses/city')) {
                    searchRequest.addFilter('(' + filte.values.map(val => `(addresses/any(address: address/city eq '${val}'))`).join(' or ') + ')');
                } else {
                    this.addFacetFilter(searchRequest, filte, filte.values);
                }
            }
        });

        if (this.regionFilter) {
            if (isNaN(this.regionFilter as any)) {
                // City Search
                searchRequest.addFilter(`addresses/any(add: add/city eq '${this.regionFilter}')`);
            } else {
                // PLZ Search
                if (options.usePostalCode) {
                    searchRequest.addFilter(`addresses/any(add: add/postalCode eq '${this.regionFilter}')`);
                }
            }
        } else {
            if (this.hasValidCoordinates()) {
                searchRequest.addFilter(
                    `addresses/any(add: geo.distance(add/location, geography'POINT(${this.coordinates?.longitude} ${this.coordinates?.latitude})') le ${this.getSearchRadius()})`,
                );
            }
        }

        await this.ensureInitialized();
        searchRequest = this.setLanguageFilter(searchRequest);
        searchRequest = this.setTechnicalFilter(searchRequest);
        searchRequest.setBrandFilter(this.getBrandFilter());

        if (options.updateFacets) {
            this.setFacets(searchRequest);
        }
        if (this.orderBy) {
            searchRequest.orderby = `${this.orderBy} ${this.isDesc ? 'desc' : ''}`;
            if ((!this.orderBy || (this.orderBy === this.defaultOptions?.defaultOrder?.orderBy)) && this.hasValidCoordinates()) {
                searchRequest.orderby = `geo.distance(location, geography'POINT(${this.coordinates?.longitude} ${this.coordinates?.latitude})')`;
            }
        }

        const top = options.pageSize || this.pageSize;
        if (this.currentPage === undefined || this.currentPage === null || this.currentPage === 0) {
            this.currentPage = 1;
        }

        const skip = (options.pageSize || this.pageSize) * (this.currentPage - 1);

        searchRequest.top = top;
        searchRequest.skip = skip;

        return new Promise((res, rej) => {
            const request: XMLHttpRequest = new XMLHttpRequest();
            request.open('POST', url);
            request.setRequestHeader('api-key', this.apiKey);
            request.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
            request.addEventListener('load', () => {
                if (request.status >= 200 && request.status < 300) {
                    const response = JSON.parse(request.response);

                    let sortedResults = response.value;
                    if (this.jobIdsFilter && this.jobIdsFilter.length) {
                        sortedResults = [];
                        for (let index = 0; index < this.jobIdsFilter.length; index++) {
                            const sortedJobId = this.jobIdsFilter[index];

                            let found = (response.value as any[]).find(x => x.jobId === sortedJobId);
                            if (found) {
                                sortedResults.push(found);
                            }
                        }
                        // response.value = response.value.sort((a: { jobId: string }, b: { jobId: string }) => {
                        //     return (this.jobIdsFilter?.indexOf(a.jobId) || 0) - (this.jobIdsFilter?.indexOf(b.jobId) || 0);
                        // });
                    }

                    const result = {
                        count: +response['@odata.count'],
                        jobs: sortedResults,
                        facets: response['@search.facets'],
                    };

                    res(result);
                    if (!options!.getAll) {
                        this.currentJobsSource.next(result);
                    }
                    if (options!.updateFacets) {
                        this.currentFacetsSource.next(response['@search.facets']);
                    }
                } else {
                    rej(request.response);
                }
            });
            request.send(JSON.stringify(searchRequest.toRequest()));
        });
    }

    private getSearchRadius(): number {
        const radius = this.searchRadius || new Distance(this.defaultRadius, (this.language || this.detectedLanguage) === 'en_US' ? Unit.Miles : Unit.Kilometers);
        return radius.inKm;
    }

    getDocsUrl() {
        if (this.useProxy) {
            return `${this.searchUrl}/${this.getIndexName()}/docs`;
        } else {
            return `${this.searchUrl}indexes/${this.getIndexName()}/docs`;
        }
    }

    async getKnownLocation(query: string) {
        const searchRequest = new SearchRequest('*');

        const isZip = /^\d+$/.test(query);
        if (isZip) {
            searchRequest.addFilter(`addresses/any(add: add/postalCode eq '${query}')`);
        } else {
            searchRequest.addFilter(`addresses/any(add: add/city eq '${query}')`);
        }
        searchRequest.top = 1;
        searchRequest.skip = 0;
        const job = (await this.search(searchRequest, false, false))?.jobs[0];
        if (job) {
            if (isZip) {
                return job.addresses?.find((a: any) => a.postalCode === query)?.location;
            } else {
                return job.addresses?.find((a: any) => a.city === query)?.location;
            }
        } else {
            return undefined;
        }
    }

    addFacetFilter(request: SearchRequest, facet: FacetItem, filterValues: string[]) {
        if (facet.isList) {
            if (facet.isComplexObject) {
                const parts = facet.name.split('/');
                request.addFilter('(' + filterValues.map(val => `${parts[0]}/any(jt: jt/${parts[1]} eq '${this.escapeFilterValue(val)}')`).join(' or ') + ')');
            } else {
                request.addFilter('(' + filterValues.map(val => `${facet.name}/any(jt: jt eq '${this.escapeFilterValue(val)}')`).join(' or ') + ')');
            }
        } else {
            request.addFilter('(' + filterValues.map(val => `${facet.name} eq '${this.escapeFilterValue(val)}'`).join(' or ') + ')');
        }
    }
    escapeFilterValue(value: string) {
        if (value.indexOf(`'`) >= -1) {
            value = value.replace(`'`, `''`);
        }
        return value;
    }
    async loadState(): Promise<{ changes: string[]; params: SearchParams }> {
        console.log('Loading search state');
        const state = sessionStorage.getItem('csb-search');
        const searchParams: SearchParams = {};
        const changes: string[] = [];
        if (state) {
            const csb: SearchParams = JSON.parse(state);
            if (csb.currentPage === 0) {
                csb.currentPage = 1;
            }
            this.coordinates = searchParams.coordinates = csb.coordinates;
            this.region = searchParams.region = csb.region;
            this.regionFilter = searchParams.regionFilter = csb.regionFilter;
            if (csb.filters) {
                csb.filters.forEach(filter => {
                    const filterMatch = this.filters?.find(f => f.name === filter.name);
                    if (filterMatch) {
                        filterMatch.values = filter.values;
                    }
                });
                searchParams.filters = this.filters;
            } else {
                searchParams.filters = this.filters;
            }

            this.keyword = searchParams.keyword = csb.keyword;

            this.currentPage = searchParams.currentPage = csb.currentPage || 1;
            this.pageSize = searchParams.pageSize = csb.pageSize || 20;

            this.orderBy = searchParams.orderBy = csb.orderBy;
            this.isDesc = searchParams.isDesc = csb.isDesc;

            if (csb.searchRadius) {
                this.searchRadius = searchParams.searchRadius = new Distance(csb.searchRadius.value, csb.searchRadius.unit);
                changes.push('searchRadius');
            }
        } else {
            searchParams.pageSize = this.pageSize;
            searchParams.orderBy = this.orderBy;
            searchParams.isDesc = this.isDesc;
        }

        const params = new URLSearchParams(new URL(window.location.href).search);
        if (params.has('region')) {
            this.region = searchParams.region = params.get('region') || '';
            await this.setLocationByRegion(this.region);
            if (!params.has('regionFilter') && this.regionFilter) {
                this.regionFilter = searchParams.regionFilter = '';
                // this.setRegionFilter('');
            }
            changes.push('region');
        }
        if (params.has('regionFilter') && params.get('regionFilter')) {
            this.regionFilter = searchParams.regionFilter = params.get('regionFilter');
            changes.push('regionFilter');
        }
        if (params.has('keyword') && params.get('keyword')) {
            this.keyword = searchParams.keyword = params.get('keyword');
            changes.push('keyword');
        }
        if (params.has('currentPage') && params.get('currentPage')) {
            this.currentPage = +(params.get('currentPage') || 0);
            if (this.currentPage === 0) {
                this.currentPage = 1;
            }
            searchParams.currentPage = this.currentPage;
            changes.push('currentPage');
        }
        if (params.has('pageSize') && params.get('pageSize')) {
            this.pageSize = searchParams.pageSize = +(params.get('pageSize') || 20);
            changes.push('pageSize');
        }
        if (params.has('orderBy') && params.get('orderBy')) {
            this.orderBy = searchParams.orderBy = params.get('orderBy');
            changes.push('orderBy');
        }
        if (params.has('isDesc') && params.get('isDesc')) {
            this.isDesc = searchParams.isDesc = params.get('isDesc') === 'true';
            changes.push('isDesc');
        }
        if (!state) {
            if (this.filters) {
                this.filters.forEach(f => {
                    f.values = [
                        ...params.getAll(f.name),
                        ...params.getAll(f.name + '[]'),
                        ...params.getAll(f.name + '[0]'),
                        ...params.getAll(f.name + '[1]'),
                        ...params.getAll(f.name + '[2]'),
                        ...params.getAll(f.name + '[3]'),
                    ];
                });
                searchParams.filters = this.filters;
                if (this.filters.some(f => f.values.length)) {
                    changes.push('filters');
                }
            }
        } else {
            if (this.filters) {
                const mergedFilters: { name: string; values: string[] }[] = [];
                this.filters.forEach(filter => {
                    mergedFilters.push({
                        name: filter.name,
                        values: [
                            ...params.getAll(filter.name),
                            ...params.getAll(filter.name + '[]'),
                            ...params.getAll(filter.name + '[0]'),
                            ...params.getAll(filter.name + '[1]'),
                            ...params.getAll(filter.name + '[2]'),
                            ...params.getAll(filter.name + '[3]'),
                        ],
                    });
                });

                if (mergedFilters.some(filter => filter.values.length)) {
                    this.filters.forEach(f => {
                        f.values = [
                            ...params.getAll(f.name),
                            ...params.getAll(f.name + '[]'),
                            ...params.getAll(f.name + '[0]'),
                            ...params.getAll(f.name + '[1]'),
                            ...params.getAll(f.name + '[2]'),
                            ...params.getAll(f.name + '[3]'),
                        ];
                    });

                    searchParams.filters = this.filters;
                    changes.push('filters');
                }
            }
        }

        return { changes: changes, params: searchParams };
    }

    persistUrl() {
        const newURL = new URL(window.location.href);
        const newParams = new URLSearchParams(newURL.search);
        newParams.delete('latitude');
        newParams.delete('longitude');
        newParams.delete('region');
        newParams.delete('regionFilter');
        newParams.delete('currentPage');
        newParams.delete('pageSize');
        newParams.delete('keyword');
        this.filters?.forEach(f => {
            newParams.delete(f.name);
        });
        newParams.delete('orderBy');
        newParams.delete('isDesc');

        if (this.region) {
            newParams.set('region', this.region);
        }
        if (this.regionFilter) {
            newParams.set('regionFilter', this.regionFilter);
        }
        if (this.currentPage) {
            newParams.set('currentPage', this.currentPage.toString());
        }
        if (this.pageSize) {
            newParams.set('pageSize', this.pageSize.toString());
        }
        if (this.keyword) {
            newParams.set('keyword', this.keyword);
        }
        this.filters?.forEach(f => {
            if (f.values.length) {
                f.values.forEach(val => newParams.append(f.name, val));
            }
        });
        if (this.orderBy) {
            newParams.set('orderBy', this.orderBy);
            newParams.set('isDesc', this.isDesc ? this.isDesc.toString() : '');
        }
        if (window.history.pushState) {
            newURL.search = newParams.toString();
            window.history.pushState({ path: newURL.href }, '', newURL.href);
        }
    }

    saveState() {
        console.log('Saving search state');
        const state = this.currentOptionsSource.value?.params;

        sessionStorage.setItem('csb-search', JSON.stringify(state));
        this.persistUrl();
    }

    async loadFacets() {
        await this.ensureInitialized();
        const url = `${this.getDocsUrl()}/search?api-version=2020-06-30`;
        let searchRequest: SearchRequest = new SearchRequest('*');

        searchRequest = this.setLanguageFilter(searchRequest);
        searchRequest = this.setTechnicalFilter(searchRequest);
        searchRequest.setBrandFilter(this.getBrandFilter());
        // searchRequest = this.setBrandFilter(searchRequest);
        searchRequest.setDatePostedFilter();
        this.setFacets(searchRequest);
        searchRequest.top = 0;
        const response = await this.post(url, searchRequest);
        this.totalCount = response?.count;
        this.currentFacetsSource.next(response?.facets);
    }

    async loadTotalCount() {

        await this.ensureInitialized();
        const url = `${this.getDocsUrl()}/search?api-version=2020-06-30`;
        let searchRequest: SearchRequest = new SearchRequest('*');
        searchRequest = this.setLanguageFilter(searchRequest);
        searchRequest = this.setTechnicalFilter(searchRequest);
        searchRequest.setBrandFilter(this.getBrandFilter());
        searchRequest.setDatePostedFilter();
        searchRequest.top = 0;
        const response = await this.post(url, searchRequest);
        this.totalCount = response?.count;
        return this.totalCount;
    }

    private setFacets(searchRequest: SearchRequest) {
        this.facets?.forEach(f => {
            // let path = f.path ? f.path + '/' : '';
            searchRequest.facets?.push(`${f.name},count:5000`);
        });
    }

    async getSearchFilterValues(filterType: string): Promise<{ key: string; value: string }[]> {
        // customerId
        return new Promise((res, rej) => {
            const request: XMLHttpRequest = new XMLHttpRequest();
            request.open('GET', `${this.apiUrl}list/${this.getIndexName()}/${filterType}`);
            request.setRequestHeader('Accept-Language', 'de-DE');
            request.addEventListener('load', () => {
                if (request.status >= 200 && request.status < 300) {
                    res(
                        (JSON.parse(request.response) as string[]).map(opt => {
                            return { key: opt, value: opt };
                        }),
                    );
                } else {
                    rej(request.response);
                }
            });
            request.send();
        });
    }

    async getAutocomplete(query: string): Promise<any> {
        return new Promise((res, rej) => {
            if (!query) {
                res({});
                return;
            }
            const suggestFields = this.config?.suggestFields;
            let filter = '';
            if (this.brand && this.brand.brand !== 'default') {
                let brands = this.getBrandFilter();
                let bFilter = brands.map(brand => `(brand eq '${brand}')`).join(' or ');

                filter = `${bFilter}`;
                // this.brandFilter = brands.map(brand => `brand eq '${brand}'`);
            }
            if (this.useLanguageFilter) {
                if (filter) {
                    filter += `and language eq '${this.language || this.detectedLanguage}'`;
                } else {
                    filter += `language eq '${this.language || this.detectedLanguage}'`;
                }
            }

            const request: XMLHttpRequest = new XMLHttpRequest();
            request.open(
                'GET',
                `${this.getDocsUrl()}/autocomplete?fuzzy=true&autocompleteMode=oneTerm&search=${query}~&api-version=2020-06-30&suggesterName=sg&queryType=full&searchFields=${suggestFields}${filter ? '&$filter=' + filter : ''
                }`,
            );
            request.setRequestHeader('api-key', this.apiKey);
            request.addEventListener('load', () => {
                if (request.status >= 200 && request.status < 300) {
                    res(JSON.parse(request.response));
                } else {
                    rej(request.response);
                }
            });
            request.send();
        });
    }

    async getRegionAutocomplete(region: string, usePostalCode = true, useBrand = true): Promise<{ value: [] }> {
        return new Promise((res, rej) => {
            if (!region) {
                res({ value: [] });
                return;
            }
            let filter = '';
            if (this.brand && useBrand) {
                filter = `&$filter=${this.brandFieldName} eq '${this.brand.brand}'`;
            }
            let postalCodeFilter = '';
            if (usePostalCode) {
                postalCodeFilter = ',addresses/postalCode';
            }

            const request: XMLHttpRequest = new XMLHttpRequest();
            request.open(
                'GET',
                `${this.getDocsUrl()}/autocomplete?fuzzy=true&autocompleteMode=oneTerm&search=${region}&api-version=2020-06-30&suggesterName=sg&searchFields=addresses/city${postalCodeFilter}${filter}`,
            );
            request.setRequestHeader('api-key', this.apiKey);
            request.addEventListener('load', () => {
                if (request.status >= 200 && request.status < 300) {
                    res(JSON.parse(request.response));
                } else {
                    rej(request.response);
                }
            });
            request.send();
        });
    }

    async getCountryCodesByBrand() {
        console.log('Current brandfilters', this.brandFilters);
        console.log('Current brand', this.brand);

        const mapToCountryCode = (country: string) => {
            switch (country) {
                case 'austria':
                    return 'AT';
                case 'slovakia':
                    return 'SK';
                case 'croatia':
                    return 'HR';
                case 'italy':
                    return 'IT';
                case 'serbia':
                    return 'RS';
                case 'bulgaria':
                    return 'BG';
                case 'hungary':
                    return 'HU';
                case 'usa':
                    return 'US';
                case 'united kingdom':
                    return 'GB';
                default:
                    return 'DE';
            }
        };

        if (this.brandFilters && this.brandFilters.length) {
            const countryFilters = this.brandFilters.map(filter => {
                return mapToCountryCode(filter.key.toLowerCase());
            });
            return countryFilters.join(',');
        } else {
            const brand = await CsbHelper.getBrand();
            this.brand = brand;
            console.log('Current brand', this.brand);
            if (this.brand && this.brand.brand && this.brand.brand !== 'default') {
                return mapToCountryCode(this.brand.brand.toLowerCase());
            } else {
                return 'DE';
            }
        }
    }

    async ensureInitialized() {
        return new Promise(res => {
            let interval = setInterval(() => {
                if (this.detectedLanguage && this.detectedIsInternal !== undefined) {
                    res(this.detectedLanguage);
                    clearInterval(interval);
                }
            }, 100);
        });
    }

    async getJob(jobId: string) {
        const url = `${this.getDocsUrl()}/search?api-version=2020-06-30`;
        const searchRequest: SearchRequest = new SearchRequest('*');
        searchRequest.addFilter(`jobId eq '${jobId}' `);
        const searchResponse = await this.post(url, searchRequest);
        return searchResponse?.jobs[0];
    }

    async getJobs(jobIds: string[]) {
        const jobIdQueries = jobIds.map(jobId => `jobId eq '${jobId}'`);
        const url = `${this.getDocsUrl()}/search?api-version=2020-06-30`;
        const searchRequest: SearchRequest = new SearchRequest('*');
        searchRequest.addFilter(jobIdQueries.join(' or '));
        const searchResponse = await this.post(url, searchRequest);
        return searchResponse?.jobs;
    }

    setLanguageFilter(searchRequest: SearchRequest): SearchRequest {
        if (this.useLanguageFilter) {
            searchRequest.addFilter(`language eq '${this.language || this.detectedLanguage}'`);
        }
        return searchRequest;
    }

    setTechnicalFilter(searchRequest: SearchRequest): SearchRequest {
        if (this.technicalFilter) {
            searchRequest.addFilter(this.technicalFilter);
        }
        return searchRequest;
    }

    private async post(url: string, searchRequest: SearchRequest): Promise<SearchResponse | undefined> {
        return new Promise<SearchResponse | undefined>((res, rej) => {
            const request: XMLHttpRequest = new XMLHttpRequest();

            request.open('POST', url);
            request.setRequestHeader('api-key', this.apiKey);
            request.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

            request.addEventListener('load', () => {
                if (request.status >= 200 && request.status < 300) {
                    const response = JSON.parse(request.response);
                    if (response.value) {
                        const result: SearchResponse = {
                            count: +response['@odata.count'],
                            jobs: response.value,
                            facets: response['@search.facets'],
                        };
                        res(result);
                    } else {
                        res(undefined);
                    }
                } else {
                    rej(request.response);
                }
            });
            request.send(JSON.stringify(searchRequest.toRequest()));
        });
    }

    async getAllJobs(select?: string[], filters?: string): Promise<Job[]> {
        let searchRequest = new SearchRequest('*');
        searchRequest.top = 1000;
        searchRequest.select = select?.join(',');
        if(filters) {
            searchRequest.addFilter(filters)
        }
        searchRequest.skip = 0;
        searchRequest.count = false;
        await this.ensureInitialized();
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (res, _rej) => {
            let response = await this.search(searchRequest);
            if (response && response.jobs.length === 1000) {
                let allJobs = [...response.jobs];
                let moreJobs = [];
                let i = 0;
                do {
                    i++;
                    searchRequest.skip = i * 1000;
                    moreJobs = (await this.search(searchRequest))?.jobs || [];
                    allJobs = [...allJobs, ...moreJobs];
                } while (moreJobs.length === 1000);
                res(allJobs);
            } else if(response) {
                res(response.jobs);
            }
        });
    }

    setSearchRadius(radius: Distance, emitChange = false) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        if (newParams.searchRadius !== radius) {
            newParams.searchRadius = radius;
            this.searchRadius = radius;
            this.currentOptionsSource.next({ changes: emitChange ? ['searchRadius'] : [], params: newParams });
        }
    }

    setOrderBy(orderBy: string, isDesc: boolean) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        if (newParams.orderBy !== orderBy || newParams.isDesc !== isDesc) {
            newParams.orderBy = orderBy;
            newParams.isDesc = isDesc;
            this.orderBy = orderBy;
            this.isDesc = isDesc;
            this.currentOptionsSource.next({ changes: ['orderBy', 'isDesc'], params: newParams });
        }
    }

    setFilter(filterName: string, filterValue: string[]) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        const filter = newParams.filters?.find(f => f.name === filterName);
        if (filter) {
            filter.values = filterValue;
        }
        newParams.currentPage = 1;
        this.currentPage = 1;
        this.currentOptionsSource.next({ changes: ['filters', 'currentPage'], params: newParams });
    }

    setJobIdsFilter(jobIds?: string[]) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        this.jobIdsFilter = jobIds;
        this.currentPage = 1;
        this.currentOptionsSource.next({ changes: ['filters', 'currentPage'], params: newParams });
    }

    clearRegion() {
        this.region = '';
    }

    clearAllFilters() {
        const newParams = { ...this.currentOptionsSource.value?.params };
        this.resetFilters();
        newParams.filters = this.filters;
        this.currentPage = 1;
        this.currentOptionsSource.next({ changes: ['filters', 'currentPage'], params: newParams });
    }

    resetFilters() {
        this.filters?.forEach(f => {
            f.values = [];
        });
    }

    setKeyword(keyword: string) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        this.keyword = newParams.keyword = keyword;
        this.currentPage = newParams.currentPage = 1;
        this.currentOptionsSource.next({ changes: ['keyword', 'currentPage'], params: newParams });
    }

    getPageSize() {
        return this.pageSize;
    }

    setPageSize(count: number, isInitial = false) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        this.pageSize = newParams.pageSize = count;
        this.currentPage = newParams.currentPage = 1;
        if (isInitial) {
            this.currentOptionsSource.next({ changes: [], params: newParams });
        } else {
            this.currentOptionsSource.next({ changes: ['pageSize', 'currentPage'], params: newParams });
        }
    }

    getCurrentPage() {
        return this.currentPage;
    }

    setCurrentPage(page: number) {
        const newParams = { ...this.currentOptionsSource.value?.params };
        this.currentPage = newParams.currentPage = page;
        this.currentOptionsSource.next({ changes: ['currentPage'], params: newParams });
    }

    async setRegion(region: string) {
        console.log(`region is ${region}`);
        if (this.region === undefined) {
            this.region = '';
        }
        if (this.region !== region) {
            const newParams = { ...this.currentOptionsSource.value?.params };
            this.region = newParams.region = region;
            this.currentOptionsSource.next({ changes: ['region'], params: newParams });
        }

        if (region) {
            await this.setLocationByRegion(region);
        } else {
            locationService.clearLocation();
        }
    }

    setRegionFilter(regionFilter: string) {
        console.log(`this: ${this.regionFilter}, para: ${regionFilter} `);
        if (this.regionFilter === undefined) {
            this.regionFilter = '';
        }
        if (this.regionFilter !== regionFilter) {
            const newParams = { ...this.currentOptionsSource.value?.params };
            this.regionFilter = newParams.regionFilter = regionFilter;
            this.currentPage = newParams.currentPage = 1;
            this.currentOptionsSource.next({ changes: ['regionFilter'], params: newParams });
        }
    }

    async setLocationByRegion(region: string) {
        if (region) {
            let countryCodes: string;
            if (this.getCountryCodes) {
                countryCodes = await this.getCountryCodes();
            } else {
                countryCodes = await this.getCountryCodesByBrand()
            }
            await locationService.setLocation(region, countryCodes);
        }
    }

    resetSearch(saveState = true) {
        this.coordinates = undefined;
        this.regionFilter = '';
        this.region = '';
        this.resetFilters();
        this.keyword = '';

        this.currentPage = 1;
        this.searchRadius = null;
        if (this.defaultOptions?.defaultOrder) {
            this.orderBy = this.defaultOptions.defaultOrder.orderBy;
            this.isDesc = this.defaultOptions.defaultOrder.isDesc;
        } else {
            this.orderBy = '';
            this.isDesc = false;
        }
        this.currentOptionsSource.next({
            changes: ['coordinates', 'region', 'regionFilter', 'filters', 'keyword', 'currentPage', 'orderBy', 'isDesc', 'searchRadius', 'all'],
            params: this.buildSearchParamsFromThis(),
        });
        if (saveState) {
            this.saveState();
        }
    }

    buildSearchParamsFromThis(): SearchParams {
        return {
            coordinates: this.coordinates,
            regionFilter: this.regionFilter,
            filters: this.filters,
            keyword: this.keyword,
            currentPage: this.currentPage,
            pageSize: this.pageSize,
            orderBy: this.orderBy,
            isDesc: this.isDesc,
            searchRadius: this.searchRadius,
        };
    }

}

export const SearchService = new SearchServiceController();
